import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Globe } from "lucide-react";
import { ElephantLogo } from "@/components/ElephantLogo";
import { useLang, STR } from "@/i18n/translations";
import { useCart } from "@/context/CartContext";
import { SocialLinks, AGENCY_SOCIALS } from "@/components/SocialIcons";

const LINKS = [
  { to: "/", key: "home" },
  { to: "/services", key: "services" },
  { to: "/artistes", key: "artists" },
  { to: "/evenements", key: "events" },
  { to: "/booking", key: "booking" },
  { to: "/boutique", key: "shop" },
  { to: "/a-propos", key: "about" },
  { to: "/contact", key: "contact" },
];

export function Navbar() {
  const { lang, setLang, t } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-gold/20 py-3" : "py-5 bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <ElephantLogo className="w-9 h-9 transition-transform duration-500 group-hover:scale-110" />
          <span className="font-serif text-2xl font-semibold tracking-wide">
            Sily <span className="text-gold">Agency</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key}`}
              className={`text-sm tracking-wide hover-underline transition-colors ${
                loc.pathname === l.to ? "text-gold" : "text-cream/80 hover:text-gold"
              }`}
            >
              {t(STR.nav[l.key])}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <SocialLinks links={AGENCY_SOCIALS} owner="Sily Agency" prefix="header-social" className="hidden xl:flex !gap-2.5 pr-3 border-r border-gold/20"
            itemClass="text-cream/70 hover:text-gold transition-colors" />
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            data-testid="language-toggle"
            className="flex items-center gap-1.5 text-xs font-semibold tracking-widest text-cream/80 hover:text-gold transition-colors"
          >
            <Globe className="w-4 h-4" />
            {lang === "fr" ? "EN" : "FR"}
          </button>

          <button
            onClick={() => setOpen(true)}
            data-testid="open-cart-btn"
            className="relative text-cream/90 hover:text-gold transition-colors"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-gold text-obsidian text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button
            className="lg:hidden text-cream"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            aria-label={mobileOpen ? t({ fr: "Fermer le menu", en: "Close menu" }) : t({ fr: "Ouvrir le menu", en: "Open menu" })}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden glass border-t border-gold/20 mt-3 px-5 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.key}`}
              className={`text-base ${loc.pathname === l.to ? "text-gold" : "text-cream/85"}`}
            >
              {t(STR.nav[l.key])}
            </Link>
          ))}
          <SocialLinks links={AGENCY_SOCIALS} owner="Sily Agency" prefix="mobile-social" className="pt-4 border-t border-gold/20"
            itemClass="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-cream/80 hover:text-gold transition-colors" />
        </nav>
      )}
    </header>
  );
}
